import { Component, OnInit, inject } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { MultiLineChartComponent, D3Helper, FuelInsightsRequest } from "../../../../_shared";
import { DropdownOption } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { FuelType } from '../../../../_shared/enums/fuel-type.enum';
import { CommonModule } from '@angular/common';
import { FuelCostKpiRequest } from '../../../models/fuel-cost-kpi-request.model';
import { FuelCostService } from '../../../services/fuel-cost.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DateInterval } from '../../../../../_shared/enums';
import { format } from 'date-fns';
import { GraphTrendIconComponent } from "../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component";
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';

@Component({
  selector: 'app-fuel-cost-widget-graph',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, GraphTrendIconComponent, EmptyStateComponent, TranslocoDirective],
  templateUrl: './fuel-cost-widget-graph.component.html',
  styleUrl: './fuel-cost-widget-graph.component.scss'
})
export class FuelCostWidgetGraphComponent extends BaseWidgetComponent implements OnInit {
  private transloco = inject(TranslocoService);

  protected lineChartData: any[] = [];
  protected xAxisConfig: any = { tickCount: 12 };

  public lineChartDieselData: any[] = [];
  public lineChartPetrolData: any[] = [];

  override isLoading = true;
  currencyCode: string = '';
  currencySymbol: string = '';
  yAxisConfig = {};

  lineConfigs: any = [];
  dropdownOptions: DropdownOption[] = [
    { label: this.transloco.translate('fuelDashboard.units.diesel'), value: FuelType.Diesel.toString(), unit: '' },
    { label: this.transloco.translate('fuelDashboard.units.petrol'), value: FuelType.Petrol.toString(), unit: '' }
  ];
  selectedUnit = FuelType.Diesel.toString();

  constructor(
    filterService: FilterService,
    private fuelCostService: FuelCostService,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
  }

  ngOnInit(): void {

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.yAxisConfig = {
      suffix: `/ℓ`,
      prefix: this.currencySymbol,
      yTickFormat: (d: number) => `${this.currencySymbol}${D3Helper.formatCompactNumber(d)}/ℓ`,
      ticks: [60, 50, 40, 30, 20, 10, 0]
    };

    this.lineConfigs = [
      { key: 'Diesel', label: this.transloco.translate('fuelDashboard.units.diesel'), color: '#FF7F00', legend: this.transloco.translate('fuelDashboard.charts.dieselCostPerLitre', { unit: this.currencySymbol }) },
      { key: 'Petrol', label: this.transloco.translate('fuelDashboard.units.petrol'), color: '#3873C9', legend: this.transloco.translate('fuelDashboard.charts.petrolCostPerLitre', { unit: this.currencySymbol }) }
    ];
  }

  public override formatValue(value: number): string {
    return `${this.currencySymbol}${value.toFixed(2)}/ℓ`;
  }

  isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  protected override onFilterChangeAsync(): Promise<void> {
    return this.loadAllFuelData();
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    await this.onFilterChangeAsync();
  }

  private async loadAllFuelData(): Promise<void> {
    this.isLoading = true;
    this.lineChartData = [];

    try {
      this.cancelSubject.next();
      await Promise.all([
        this.loadFuelCostSummaryData(),
        this.loadDieselFuelCostPerVolumeInsightsData(),
        this.loadPetrolFuelCostPerVolumeInsightsData(),
      ])
      this.loadGraphData()
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private async loadFuelCostSummaryData(): Promise<void> {
    const request: FuelCostKpiRequest = {
      fuelType: this.getEnumFromString(this.selectedUnit),
      accountIds: this.widgetFilter.accountIds,
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
      previousTimeTo: this.widgetFilter.previousCycleEndDate!
    };

    const response = await this.fuelCostService.getFuelCostSummary(request, this.cancelSubject);
    if (response.data) {
      this.value = response.data.totalValue;
      this.percentageDifference = response.data.percentageChange;
      this.difference = response.data.difference;
    }
  }

  private async loadDieselFuelCostPerVolumeInsightsData(): Promise<void> {
    const request: FuelInsightsRequest = {
      fuelType: FuelType.Diesel,
      accountIds: this.widgetFilter.accountIds,
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      interval: this.widgetFilter.interval!
    };

    const response = await this.fuelCostService.getFuelCostPerVolumeInsights(request, this.cancelSubject);
    if (response && response.data) {
      this.lineChartDieselData = response.data;
    }
  }

  private async loadPetrolFuelCostPerVolumeInsightsData(): Promise<void> {
    const request: FuelInsightsRequest = {
      fuelType: FuelType.Petrol,
      accountIds: this.widgetFilter.accountIds,
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      interval: this.widgetFilter.interval!
    };

    const response = await this.fuelCostService.getFuelCostPerVolumeInsights(request, this.cancelSubject);
    if (response && response.data) {
      this.lineChartPetrolData = response.data;
    }
  }

  public hasData(): boolean {
    if (!this.lineChartData || this.lineChartData.length === 0) return false;

    return this.lineChartData.some(item => {
      const dieselValue = Number(item['Diesel']) || 0;
      const petrolValue = Number(item['Petrol']) || 0;
      return dieselValue > 0 || petrolValue > 0;
    });
  }

  formatPercentChange(): string {
    if (this.percentageDifference != null) {
      const sign = this.percentageDifference > 0 ? '+' : '';
      return `${sign}${this.percentageDifference.toFixed(2)}%`;
    }

    return this.transloco.translate('fuelShared.charts.notAvailable');
  }

  getArrowIcon(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0
        ? 'assets/images-fuel/yellowArrowIcon.svg'
        : 'assets/images-fuel/greenArrowIcon.svg';
    }
    return 'assets/images-fuel/yellowArrowIcon.svg';
  }

  getPercentChangeClass(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0 ? 'percent-increase' : 'percent-decrease';
    }

    return '';
  }

  private getEnumFromString(value: string): FuelType {
    const enumValue = parseInt(value) as FuelType;
    const isValid = Object.values(FuelType).includes(enumValue);
    return isValid ? enumValue : FuelType.Diesel;
  }

  private loadGraphData(): void {
    this.processInsights(this.lineChartDieselData, this.lineChartPetrolData);
  }

  private processInsights(dieselData: any[], petrolData: any[]): void {
    this.lineChartData = [];

    if (!dieselData?.length && !petrolData?.length) {
      return;
    }

    const maxLength = Math.max(dieselData?.length || 0, petrolData?.length || 0);

    for (let i = 0; i < maxLength; i++) {
      const dieselItem = dieselData?.[i];
      const petrolItem = petrolData?.[i];

      const timestamp = new Date(
        dieselItem?.timeSeries ||
        petrolItem?.timeSeries ||
        new Date()
      );

      this.lineChartData.push({
        date: this.createChartDateString(timestamp, i),
        Diesel: dieselItem?.totalValue ?? 0,
        Petrol: petrolItem?.totalValue ?? 0,
        originalTimestamp: timestamp,
        displayLabel: this.is24HourView()
          ? this.formatToHourly(timestamp)
          : timestamp.toISOString().split('T')[0],
        tooltipDateTime: this.formatTooltipDateTime(timestamp)
      });
    }

    this.lineChartData.sort(
      (a, b) => a.originalTimestamp.getTime() - b.originalTimestamp.getTime()
    );

    this.updateXAxisConfig();
  }

  private is24HourView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Day;
  }

  private isWeekView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Week;
  }

  private isMonthView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Month;
  }

  private formatToHourly(date: Date): string {
    return format(date, 'haaa').replace('AM', 'am').replace('PM', 'pm');
  }

  private formatTooltipDateTime(date: Date): string {
    return format(date, 'd MMM HH:mm');
  }

  private formatXAxisStyle(date: Date): string {
    if (this.is24HourView()) {
      return format(date, 'haaa').replace('AM', 'am').replace('PM', 'pm');
    } else if (this.isWeekView() || this.isMonthView()) {
      return format(date, 'dd MMM');
    } else {
      return format(date, 'MMM');
    }
  }

  private updateXAxisConfig(): void {
    if (this.is24HourView()) {
      this.xAxisConfig = {
        tickCount: 12,
        isHourlyView: true,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    } else if (this.isWeekView() || this.isMonthView()) {
      this.xAxisConfig = {
        tickCount: 4,
        isHourlyView: false,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    } else {
      this.xAxisConfig = {
        tickCount: 4,
        isHourlyView: false,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    }
  }

  private createChartDateString(date: Date, index: number): string {
    if (this.is24HourView()) {
      return format(date, "yyyy-MM-dd'T'HH:00:00");
    } else {
      return format(date, 'yyyy-MM-dd');
    }
  }
}